/**
 * Listing prioritization in OpenSearch: prepend brand script sort, then user/default field sort,
 * only when the effective sort still matches the listing default (see `utils/prioritization.ts`).
 */

import { Sort } from '@opensearch-project/opensearch/api/_types/_common';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import type { ResolvedPrioritization } from '@/utils';

export type MergeListingSortWithPrioritizationArgs = {
  prioritization: ResolvedPrioritization | undefined;
  /** OpenSearch sort for the listing query (search params). */
  effectiveSort: Sort | undefined;
  /** Prismic-configured default for this listing. */
  listingDefaultSort: Sort | undefined;
  hasSearchText: boolean;
};

/** Rank sent for non-prioritized brands / missing brand (sort asc: lower first). */
export const OPENSEARCH_LISTING_PRIORITY_TAIL = 100_000;

const BRAND_SORT_SCRIPT = `
if (!doc.containsKey('brand.keyword') || doc['brand.keyword'].size() == 0) {
  return params.tail;
}
String k = doc['brand.keyword'].value;
String canon = k;
int sp;
while ((sp = canon.indexOf(' ')) >= 0) {
  canon = canon.substring(0, sp) + '_' + canon.substring(sp + 1);
}
canon = canon.toUpperCase();
def br = params.brandRanks;
if (br != null && br.containsKey(canon)) {
  return br.get(canon);
}
return params.tail;
`.trim();

const buildBrandPrioritizationSortKey = (
  prioritization: ResolvedPrioritization | undefined,
): Record<string, unknown> | undefined => {
  if (!prioritization?.enabled) {
    return undefined;
  }

  const ranks = prioritization.brandPriorityMap;
  if (!Object.keys(ranks).length) {
    return undefined;
  }

  return {
    _script: {
      type: 'number',
      order: 'asc',
      script: {
        lang: 'painless',
        source: BRAND_SORT_SCRIPT,
        params: {
          brandRanks: ranks,
          tail: OPENSEARCH_LISTING_PRIORITY_TAIL,
        },
      },
    },
  };
};

/**
 * Prepends brand prioritization script sort when the listing uses implicit CMS default sort:
 * no `sort` on the request URL (`sortFromRequestUrl` absent) and effective sort matches default.
 * Parallels `resolveSearchParams`: URL `sort` means user-visible choice even if it matches Prismic.
 */
export const mergeListingSortWithPrioritization = ({
  prioritization,
  effectiveSort,
  listingDefaultSort,
  hasSearchText,
}: MergeListingSortWithPrioritizationArgs): Sort | undefined => {
  const base: Sort | undefined =
    effectiveSort ??
    (listingDefaultSort && !hasSearchText ? listingDefaultSort : undefined);

  const userOverrodeListingDefault =
    effectiveSort != null && !isEqual(effectiveSort, listingDefaultSort);

  const sortProvidedOnRequestUrl = !isEmpty(effectiveSort);

  const prioritizationForMerge: ResolvedPrioritization | undefined =
    sortProvidedOnRequestUrl || userOverrodeListingDefault
      ? undefined
      : prioritization;

  const prioKey = buildBrandPrioritizationSortKey(prioritizationForMerge);

  if (!prioKey) {
    return base;
  }

  const prioFirst = [prioKey];
  if (!base) {
    return prioFirst as Sort;
  }

  const baseArr = Array.isArray(base) ? base : [base];
  return [...prioFirst, ...baseArr] as Sort;
};
